import { css, keyframes } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import { wacToken } from "@/libraries/design/foundations";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src";
import { DropdownType } from "@/patterns/global-nav/src/mobile-nav/mobile-nav";

import { useMobileNavDisplay } from "../../global-nav/src/hooks/use-mobile-nav-display";
import { Divider } from "../../global-nav/src/mobile-nav/icons/divider";
import { MobileNavLinksContainer } from "../../global-nav/src/mobile-nav/mobile-nav-links-container";

import { CategoriesDropdown } from "./subnav-categories-dropdown";
import { DevopsDropdown } from "./subnav-devops-dropdown";
import { MobileSubNavTop } from "./subnav-mobile-top";

import type { MobileNavLinksContainerInfo } from "../../global-nav/src/mobile-nav/mobile-nav-links-container";

const containerCss = css({
    borderBlockStart: `1px solid ${wacToken("color.neutral.light300")}`,
    insetBlockStart: "56px",
    background: wacToken("color.neutral.light0"),
    display: "flex",
    flexDirection: "column",
    gap: token("space.150"),
    height: "57px",
    justifyContent: "center"
});

// @ts-ignore
const navBarShadow = css({
    height: "72px",
    // eslint-disable-next-line local/ensure-wac-token-usage
    boxShadow: "0 2px 10px rgba(0, 0, 0, .25)"
});

const shownInnerMobileDropdownMenuStyle = css({
    display: "block",
    margin: "8px 16px"
});

const hiddenInnerMobileDropdownMenuStyle = css({
    display: "none",
    margin: "8px 16px"
});

const menuContentStyle = css({
    display: "none",
    width: "auto",

    [media.above.sm]: {
        display: "none"
    }
});

const containerClosedCss = css({
    padding: "8px"
});

const containerOpenCss = css({
    padding: "8px",
    [media.below.sm]: {
        height: "100vh",
        width: "100%",
        overflow: "auto",
        position: "fixed",
        padding: "8px 24px 24px 24px",
        zIndex: "5"
    }
});

const navBarClosedCss = css({
    paddingInline: token("space.200"),
    display: "flex",
    justifyContent: "space-between"
});

// @ts-ignore
const alignNavBarText = css({
    alignItems: "center",
    flexFlow: "row wrap"
});

const navBarOpenCss = css({
    [media.above.sm]: {
        paddingInline: token("space.200")
    },
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between"
});

const fadeIn = keyframes({
    from: {
        opacity: "0"
    },
    to: {
        opacity: "1"
    }
});

const slideInLeft = keyframes({
    from: {
        transform: "translateX(-100%)",
        opacity: "0"
    },
    to: {
        transform: "translateX(0%)",
        opacity: "1"
    }
});

const slideInRight = keyframes({
    from: {
        transform: "translateX(100%)",
        opacity: "0"
    },
    to: {
        transform: "translateX(0%)",
        opacity: "1"
    }
});

const fadeInCss = css({
    opacity: "0",
    animation: `0.4s ease-in ${fadeIn} 200ms forwards`
});

const slideInLeftCss = css({
    opacity: "0",
    animation: `0.5s ${slideInLeft} forwards`
});

const slideInRightCss = css({
    opacity: "0",
    animation: `0.5s ${slideInRight} forwards`
});

export type MobileSubNavProps = {
    headerLinkText: string;
    headerLinkHref: string;
    productKey: ProductKeyTypes;
    isShowSearchButton?: boolean;
    isSubNav?: boolean;
    mobileLinks: MobileNavLinksContainerInfo[];
};

export type MobileGlobalNavMenuProps = {
    isDisplayLoggedInUseDropdown: boolean;
};

export type MobileGlobalNavDropdownMenuProps = {
    readonly showMenu: boolean;
    readonly isUserLoggedIn: boolean;
    readonly menuPage: DropdownType;
    readonly firstOpen: boolean;
    readonly mobileLinksSections: MobileNavLinksContainerInfo[];
    handleToggleMenu: (showMenu: boolean) => void;
    handleMenuPage: (menuPage: DropdownType | string) => void;
    handleFirstOpen: (firstOpen: boolean) => void;
    productKey?: ProductKeyTypes;
};

export const MobileSubNavDropdownMenu = ({
    menuPage = "default",
    firstOpen,
    mobileLinksSections,
    handleMenuPage
}: MobileGlobalNavDropdownMenuProps) => {
    return (
        <>
            <div
                css={[
                    firstOpen && fadeInCss,
                    !firstOpen && slideInRightCss,
                    menuPage === "default"
                        ? shownInnerMobileDropdownMenuStyle
                        : hiddenInnerMobileDropdownMenuStyle
                ]}
                className="inner-mobile-dropdown-menu"
            >
                {mobileLinksSections.map(
                    ({ mobileLinkKey, mobileLinksProps }, index) => (
                        <MobileNavLinksContainer
                            key={index}
                            mobileLinkKey={mobileLinkKey}
                            mobileLinksProps={mobileLinksProps}
                            handleMenuPage={handleMenuPage}
                            isIncludeBottomDivider={false}
                        />
                    )
                )}
            </div>
            <div
                css={[
                    slideInLeftCss,
                    menuPage === "devops"
                        ? shownInnerMobileDropdownMenuStyle
                        : hiddenInnerMobileDropdownMenuStyle
                ]}
                className="inner-mobile-dropdown-menu"
            >
                <DevopsDropdown />
            </div>
            <div
                css={[
                    slideInLeftCss,
                    menuPage === "categories"
                        ? shownInnerMobileDropdownMenuStyle
                        : hiddenInnerMobileDropdownMenuStyle
                ]}
                className="inner-mobile-dropdown-menu"
            >
                <CategoriesDropdown />
            </div>
        </>
    );
};

const textWrapperCss = css({
    height: "100%",
    color: wacToken("color.primary.black"),
    alignItems: "center",
    display: "flex",
    flexDirection: "row"
});

const textCss = css({
    fontSize: "1.25rem",
    padding: "26px 16px 20px 0",
    fontWeight: 600
});

// @ts-ignore
const ffTextCss = css({
    fontSize: "1.5rem",
    color: wacToken("color.extended.blue1000"),
    fontFamily: `"Charlie Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Noto Sans", Ubuntu, "Droid Sans", "Helvetica Neue", sans-serif`,
    fontWeight: 500
});

export const MobileSubNav = ({
    headerLinkText,
    headerLinkHref,
    productKey,
    mobileLinks
}: MobileSubNavProps) => {
    const {
        userInfo,
        showMenu,
        firstOpen,
        menuPage,
        handleToggleMenu,
        handleFirstOpen,
        handleMenuPage
    } = useMobileNavDisplay(false);

    const headerLink = (
        <div css={textWrapperCss}>
            <span css={textCss}>{headerLinkText}</span>
        </div>
    );

    const mobileGlobalNavDropdownMenu = () => (
        <MobileSubNavDropdownMenu
            showMenu={showMenu}
            isUserLoggedIn={userInfo.isUserLoggedIn}
            menuPage={menuPage as DropdownType}
            firstOpen={firstOpen}
            mobileLinksSections={mobileLinks}
            handleToggleMenu={handleToggleMenu}
            handleMenuPage={handleMenuPage}
            handleFirstOpen={handleFirstOpen}
            productKey={productKey}
        />
    );

    return (
        <div
            css={[
                containerCss,
                showMenu ? containerOpenCss : containerClosedCss
            ]}
        >
            <div css={[showMenu ? navBarOpenCss : navBarClosedCss]}>
                <MobileSubNavTop
                    productKey={productKey}
                    showMenu={showMenu}
                    handleToggleMenu={handleToggleMenu}
                    menuPage={menuPage as DropdownType}
                    handleMenuPage={handleMenuPage}
                    handleFirstOpen={handleFirstOpen}
                    mobileLogo={headerLink}
                    mobileLogoAriaLabel={headerLinkText}
                    mobileLogoHref={headerLinkHref}
                    mobileLogoDataEventContainer={"subNavWac"}
                    tabletDropdownMenu={mobileGlobalNavDropdownMenu()}
                />
            </div>
            <div
                css={[menuContentStyle, showMenu && css({ display: "block" })]}
                id="nav-mobile-dropdown-menu"
            >
                <div>
                    <Divider />
                </div>
                {mobileGlobalNavDropdownMenu()}
            </div>
        </div>
    );
};
